import Script from "next/script";
import { ChevronRightOutlined as ChevronRight } from "@JohnLewisPartnership/jl-design-system/dist/components/icons/ChevronRightOutlined";
import { formatAmplienceImageURL } from "@JohnLewisPartnership/jl-design-system/dist/utils/imageUrlModifier/imageUrlModifier";
import styles from "./PaymentPlanKlarna.module.scss";
import { RuntimeConfig } from "@/types/types";

const KLARNA_WORDMARK_URL =
  "https://static.johnlewiscontent.com/v1/static/Wordmark-Transparent-And-Black 2";
const KLARNA_LEARN_MORE_URL =
  "https://www.johnlewis.com/our-services/paymentplans/klarna";
const KLARNA_PLACEMENT_ID = "paymentPlansKlarnaPlacement";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace React.JSX {
    export interface IntrinsicElements {
      "klarna-placement": {
        id: string;
        "data-key": string;
        "data-locale": string;
        "data-purchase-amount"?: string;
      };
    }
  }
}

export type PaymentPlanKlarnaProps = {
  klarnaAmountPence?: string;
  showPlaceholder?: boolean;
  runtimeConfig?: RuntimeConfig;
  onClick?: () => void;
};

export function PaymentPlanKlarna({
  klarnaAmountPence,
  showPlaceholder,
  runtimeConfig,
  onClick,
}: PaymentPlanKlarnaProps) {
  if (showPlaceholder) {
    return (
      <a
        className={styles.placeholder}
        href={KLARNA_LEARN_MORE_URL}
        aria-label="Learn more about Klarna"
        onClick={onClick}
        data-component="PaymentPlanKlarnaPlaceholder"
        data-testid="payment-plans-klarna-placeholder"
      >
        <span className={styles.icon}>
          <img
            src={formatAmplienceImageURL(KLARNA_WORDMARK_URL)}
            alt="Klarna"
            className={styles.klarnaLogo}
            data-testid="payment-plans-klarna-icon"
          />
        </span>
        <span className={styles.text}>
          <span className={styles.row}>
            <span className={styles.label}>Pay with Klarna</span>
          </span>
          <span className={styles.legal}>
            18+, T&C apply, Credit subject to status.
          </span>
        </span>
        <ChevronRight size="medium" className={styles.chevron} />
      </a>
    );
  }

  return (
    <div
      className={styles.offer}
      data-testid="payment-plans-klarna"
      data-component="PaymentPlanKlarna"
      onClickCapture={onClick}
    >
      <Script
        async
        data-environment={runtimeConfig?.klarnaEnvironment}
        src={runtimeConfig?.klarnaScriptURL}
        data-client-id={runtimeConfig?.klarnaClientId}
      />
      <style>
        {`
          #${KLARNA_PLACEMENT_ID}::part(osm-container) {
            padding-left: 0;
            padding-right: 0;
            font-size: 14px;
            font-family: GillSansforJL, Gill Sans, Gill Sans MT, GillSansMTStd-Medium, Avenir, Corbel, Arial, Helvetica, sans-serif;
            color: #141414;
          }

          #${KLARNA_PLACEMENT_ID}::part(osm-legal) {
            color: #6f6f6f;
            text-align: left;
            font-size: 12px;
            line-height: 18px;
          }

          #${KLARNA_PLACEMENT_ID}::part(osm-cta) {
            position: absolute;
            inset: 0;
            color: transparent;
            text-decoration-color: transparent;
            forced-color-adjust: none;
          }
        `}
      </style>
      <span className={styles.icon}>
        <img
          src={formatAmplienceImageURL(KLARNA_WORDMARK_URL)}
          alt="Klarna"
          className={styles.klarnaLogo}
          data-testid="payment-plans-klarna-icon"
        />
      </span>
      <span className={styles.text}>
        <klarna-placement
          id={KLARNA_PLACEMENT_ID}
          data-key="credit-promotion-auto-size"
          data-locale="en-GB"
          data-purchase-amount={klarnaAmountPence}
        />
      </span>
      <ChevronRight size="medium" className={styles.chevron} />
    </div>
  );
}
